import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { lessonById } from '../lib/soundLessons';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi, collectionsApi, onboardingApi, challengeApi } from '../lib/api';
import ChallengeCountdown, { CHALLENGE_STARTS } from '../components/ChallengeCountdown';
import DashboardCustomise, { orderSections } from '../components/DashboardCustomise';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import PracticeSessionDialog from '../components/PracticeSessionDialog';
import { toast } from 'sonner';
import {
    Mic, BookOpen, Target, Play, FolderOpen,
    Flame, Trophy, TrendingUp, CheckCircle2, Check, Minus, Star, X, Bell, Sparkles, Plus, ArrowRight, ArrowUp, HelpCircle, Lock, Gift, Download, SlidersHorizontal
} from 'lucide-react';
import { settingsApi } from '../lib/api';
import { useInstallPrompt } from '../lib/installPrompt';
import { getSavedSessionLength, getSavedFlow, saveFlow, flowPath, getSavedOrder } from '../lib/speech';
import { getScoreColor, pronunciationBeta } from '../lib/score';
import { canCheckout, PRICING_URL } from '../lib/billing';
import { MimiqueIcon } from '../components/MimiqueLogo';
import { WordPlayButton } from '../components/PronunciationTips';
import { UpgradeDialog, UpgradeButtons } from '../components/ProUpgrade';
import FoundingBadge from '../components/FoundingBadge';
import PlanBadge from '../components/PlanBadge';

// ── Helpers ─────────────────────────────────────────────────────────────────

const heatColor = (count) => {
    if (count === 0) return 'bg-stone-100';
    if (count <= 2) return 'bg-primary/20';
    if (count <= 5) return 'bg-primary/50';
    return 'bg-primary';
};

// ── Sub-components ───────────────────────────────────────────────────────────

// The customisable sections, in the learner's order, two columns on a wide
// screen. A half-width section left with no partner in its row (its partner
// hidden, or moved next to a full-width one) widens to fill the row, so
// there is never a gap beside it.
const SectionGrid = ({ order, children }) => {
    const shown = [children].flat(Infinity)
        .filter(s => s && s.node)
        .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
    const widen = new Set();
    let alone = null;
    for (const s of shown) {
        if (s.full) {
            if (alone) widen.add(alone);
            alone = null;
        } else {
            alone = alone ? null : s.id;
        }
    }
    if (alone) widen.add(alone);
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {shown.map(s => (
                <div key={s.id} className={s.full || widen.has(s.id) ? 'md:col-span-2' : ''}>
                    {s.node}
                </div>
            ))}
        </div>
    );
};

const StatCard = ({ icon: Icon, iconBg, iconColor, value, valueClass, label, action, onClick }) => (
    <Card
        className={`bg-white border-stone-100 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md hover:border-primary/30 active:scale-[0.98] transition-all' : ''}`}
        onClick={onClick}
    >
        <CardContent className="p-3 flex flex-col items-center justify-center gap-1.5 text-center">
            <div className={`w-9 h-9 ${iconBg} rounded-xl flex items-center justify-center`}>
                <Icon className={`h-5 w-5 ${iconColor}`} />
            </div>
            <p className={`text-2xl font-bold leading-none ${valueClass || 'text-foreground'}`}>{value}</p>
            <p className="text-xs text-stone-500 leading-tight">{label}</p>
            {action}
        </CardContent>
    </Card>
);

// "Practise these..." under a lowest-scores list: the phrases card and the
// words card, so the two buttons cannot drift apart.
const PractiseListButton = ({ testId, onClick, children }) => (
    <div className="flex justify-center sm:justify-start mt-4">
        <Button
            size="sm"
            data-testid={testId}
            className="rounded-full text-xs h-7 px-3 bg-accent hover:bg-accent/90 text-accent-foreground"
            onClick={onClick}
        >
            <Play className="h-3 w-3 mr-1" /> {children}
        </Button>
    </div>
);

// One word from "words with lowest scores", with its score and the model voice.
//
// The number is the word's average inside real phrases, across every phrase it
// appears in. It used to be replaced by a solo drill score, recorded here on
// its own. That is gone: a word said alone is an easier test than the same
// word in a sentence, so the two numbers were never comparable, and the drill
// taught people to chase a number instead of the sentence it came from.
//
// Hearing themselves back belongs on the practice screens, where their own
// recording exists and the word can be played out of it.
const WeakWordRow = ({ word }) => (
    <div className="flex items-center gap-2 py-1">
        <span className="flex-1 min-w-0 truncate text-sm text-foreground font-medium">{word.word}</span>
        <span className={`w-10 flex-shrink-0 text-right text-sm font-semibold tabular-nums ${
            getScoreColor(Math.round(word.score))}`}>
            {Math.round(word.score)}%
        </span>
        <WordPlayButton word={word.word} />
    </div>
);

// One sound and how it scores across all their recordings, named the way the
// result cards name it, with its tip and a link to its lesson when there is one.
const WeakSoundRow = ({ sound }) => (
    <div className="py-1">
        <div className="flex items-center gap-2">
            <span className="flex-1 min-w-0 text-sm text-foreground font-medium">{sound.label}</span>
            <span className={`w-10 flex-shrink-0 text-right text-sm font-semibold tabular-nums ${
                getScoreColor(Math.round(sound.score))}`}>
                {Math.round(sound.score)}%
            </span>
        </div>
        {/* Tip and lesson link here and on the summary; tip only mid-session
            (Leanne, 27 Oct 2026). */}
        {sound.hint && <p className="text-[11px] leading-snug text-stone-500">{sound.hint}</p>}
        {/* The lesson for this sound, when there is one (Leanne, 27 Oct 2026). */}
        {(() => {
            const lesson = lessonById(sound.lesson);
            return lesson && (
                <Link to={`/sounds/${lesson.id}`} data-testid="weak-sound-lesson-link"
                      className="text-xs font-semibold text-primary underline underline-offset-2">
                    Practise: {lesson.name}
                </Link>
            );
        })()}
    </div>
);

// The "lowest scores" cards, words and sounds alike, so the two can never look
// different. Free sees it locked rather than not at all: hiding it means they
// never learn it exists. The locked rows are ordinary examples with invented
// numbers, so nothing pretends to be a measurement they have not been given.
const LowestScoresCard = ({ title, subtitle, isFreePlan, lockedRows, upgradeTitle, upgradeMessage,
                            upgradeOpen, setUpgradeOpen, listHeight = 'max-h-56', footer = null, children }) => (
    <Card className="bg-white border-stone-100 shadow-sm">
        <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
                {/* Amber lock, the same one every other locked feature uses. */}
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                    isFreePlan ? 'bg-amber-100' : 'bg-primary/10'}`}>
                    {isFreePlan
                        ? <Lock className="h-3.5 w-3.5 text-amber-600" />
                        : <Mic className="h-3.5 w-3.5 text-primary" />}
                </div>
                <span className="text-sm font-semibold text-stone-700">{title}</span>
                {/* Beside the heading rather than under the list: the list is
                    the thing being withheld, so the way past it belongs at the
                    top, not after it. */}
                {isFreePlan && canCheckout() && (
                    <Button size="sm" onClick={() => setUpgradeOpen(true)}
                            className="rounded-full text-xs h-7 px-3 ml-auto">
                        Unlock
                    </Button>
                )}
            </div>
            {isFreePlan ? (
                <>
                    <div className="space-y-2.5 blur-[3px] select-none" aria-hidden="true">
                        {lockedRows.map(([label, n]) => (
                            <div key={label} className="flex items-center justify-between gap-3">
                                <span className="text-sm text-foreground font-medium">{label}</span>
                                <span className="text-sm font-semibold text-stone-400">{n}%</span>
                            </div>
                        ))}
                    </div>
                    <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen}
                                   title={upgradeTitle} message={upgradeMessage} />
                </>
            ) : (
                <>
                    <p className="text-xs text-stone-500 -mt-2 mb-3">{subtitle}</p>
                    <div className={`space-y-1 ${listHeight} overflow-y-auto pr-1`}>{children}</div>
                    {footer}
                </>
            )}
        </CardContent>
    </Card>
);


// The 30-day challenge, for the handful of people taking part.
//
// One job: show the same set of phrases scored week after week, so the line
// moves. Their ordinary average cannot do that, because it mixes in everything
// else they practise and it never forgets a bad first attempt.
//
// The calendar further down already answers "did I turn up", so this does not
// repeat it.
// The thirty days as squares, start to finish.
//
// The progress bar alone says almost nothing on day 2. This says the whole
// shape of the month at a glance: what has been done, what was missed, and how
// much is left.
//
// Deliberately not the dashboard heatmap. That one runs in calendar weeks over
// the last 90 days; this runs from their own start date to their own day 30,
// which is the only frame the challenge is judged in. Amber rather than green,
// so it reads as part of the challenge and not as the streak calendar.
//
// No legend: filled, pale and empty read as more, less and none without being
// told.
const ChallengeCalendar = ({ days, target, onPractise }) => {
    if (!days?.length) return null;
    return (
        // Six across on a phone, ten on a tablet, fifteen on a desktop, which
        // divides thirty into two clean rows and fills the card.
        //
        // Capping the width instead was worse: the squares stayed the right
        // size but left a band of empty card beside them. More columns on a
        // wider screen keeps the squares sensible AND uses the space.
        <div className="grid grid-cols-6 sm:grid-cols-10 lg:grid-cols-[repeat(15,minmax(0,1fr))] gap-1.5 mt-4">
            {days.map(d => {
                const done = d.minutes >= target;
                const part = !done && d.minutes > 0;
                const missed = !d.future && !done && !part && !d.today;
                // Today, nothing done yet: the square becomes the way in.
                const start = d.today && !done && !part;

                const body = (
                    <>
                        <span className={`text-[11px] font-semibold leading-none ${
                            d.future ? 'text-stone-300'
                                : done ? 'text-amber-700'
                                    : missed ? 'text-rose-300'
                                        : 'text-stone-500'}`}>
                            {d.day}
                        </span>
                        <span className="h-4 flex items-center justify-center">
                            {done ? <Check className="h-4 w-4 text-amber-500" strokeWidth={3} />
                                : start ? <Play className="h-3.5 w-3.5 text-primary fill-current" />
                                    : part ? <Minus className="h-4 w-4 text-amber-400" strokeWidth={3} />
                                        : missed ? <X className="h-3.5 w-3.5 text-rose-400" strokeWidth={3} />
                                            : null}
                        </span>
                        {/* A part day is the only one where the mark alone
                            says nothing useful: the minutes are what tells
                            them how close they got to the target. */}
                        {part && (
                            <span className="text-[9px] leading-none text-amber-600 tabular-nums">
                                {d.minutes < 1 ? '<1' : Math.round(d.minutes)}m
                            </span>
                        )}
                    </>
                );

                const shell = `aspect-square rounded-lg border flex flex-col items-center
                    justify-center gap-0.5 ${
                    d.future ? 'bg-stone-50 border-stone-100'
                        : done ? 'bg-amber-50 border-amber-200'
                            : part ? 'bg-white border-amber-200'
                                : missed ? 'bg-white border-rose-100'
                                    : 'bg-white border-stone-200'}
                    ${d.today ? 'ring-2 ring-primary ring-offset-1' : ''}`;

                return start ? (
                    <button key={d.day} type="button" onClick={onPractise}
                            aria-label={`Day ${d.day}, start today's practice`}
                            className={`${shell} hover:bg-primary/5 transition-colors`}>
                        {body}
                    </button>
                ) : (
                    <div key={d.day} className={shell}>{body}</div>
                );
            })}
        </div>
    );
};

// Same flame as the badge, so the challenge reads as one thing wherever it
// appears. Shared by every challenge card.
const ChallengeLabel = () => (
    <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold inline-flex items-center gap-1">
        <Flame className="h-3 w-3 fill-current text-amber-500" />
        30 day challenge
    </p>
);

// Before day 1, for anyone with a November place: the countdown, in the spot
// the challenge card takes over on the day. Same amber card, same flame.
const ChallengeCountdownCard = () => (
    <Card className="bg-white border-amber-200 shadow-sm">
        <CardContent className="p-4 sm:p-5">
            <ChallengeLabel />
            <p className="text-lg font-bold text-foreground leading-tight">Starts Monday 2 November</p>
            <div className="mt-3">
                <ChallengeCountdown onDark={false} />
            </div>
            <p className="text-xs text-stone-500 mt-3">
                Your challenge phrases and 30-day tracker will be here on day 1.
            </p>
        </CardContent>
    </Card>
);

// Taking part, but their tailored set is not made yet: it is built from their
// first recordings. Their 30 days start the day it arrives, so `day` is null
// until then.
const ChallengeWaitingCard = ({ day, length, onPractise }) => (
    <Card className="bg-white border-amber-200 shadow-sm">
        <CardContent className="p-4 sm:p-5">
            <ChallengeLabel />
            <p className="text-lg font-bold text-foreground leading-tight">
                {day ? `Day ${day} of ${length}` : 'Starts when your phrases are ready'}
            </p>
            <p className="text-sm text-stone-600 mt-2">
                Your challenge phrases are on their way. They're made just for you, from
                the sounds your recordings show you find hardest.
            </p>
            <p className="text-sm text-stone-600 mt-2">
                Do a couple of practice sessions on any phrases so I can hear where to focus.
                Your challenge phrases appear here as soon as they're ready
                {day ? '.' : ', and your 30 days start that day.'}
            </p>
            <Button onClick={onPractise}
                    className="w-full mt-3 bg-amber-500 hover:bg-amber-600 text-white">
                <Play className="h-4 w-4 mr-2 fill-current" />
                Practise now
            </Button>
        </CardContent>
    </Card>
);

const ChallengeCard = ({ challenge, onPractise }) => {
    const { day, length, weeks, baseline, change } = challenge;

    return (
        <Card className="bg-white border-amber-200 shadow-sm">
            <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <ChallengeLabel />
                        <p className="text-lg font-bold text-foreground leading-tight">
                            Day {day} of {length}
                        </p>
                    </div>
                    {change != null && (
                        <div className="text-right">
                            <p className={`text-2xl font-bold leading-none ${
                                change >= 0 ? 'text-emerald-600' : 'text-stone-500'}`}>
                                {change >= 0 ? '+' : ''}{change}%
                            </p>
                            <p className="text-xs text-stone-500">since week 1</p>
                        </div>
                    )}
                </div>

                {challenge.focus_sounds?.length > 0 && (
                    <p className="text-xs text-stone-500 mt-2">
                        Your phrases focus on the following sounds:{' '}
                        <span className="font-medium text-foreground">{challenge.focus_sounds.join(', ')}</span>
                    </p>
                )}

                {/* Only says something when there is something to do. Once the
                    week's run is done the blocks above say it better. The
                    arrow in the current week block is what it points at. */}
                {baseline == null ? (
                    <p className="text-xs text-stone-500 mt-2">
                        Run your challenge phrases to set your starting score.
                    </p>
                ) : !challenge.done_this_week ? (
                    <p className="text-xs font-semibold text-amber-700 mt-2">
                        Let's see if you have improved this week.
                    </p>
                ) : null}

                {/* One block a week, the last one being the day 30 retest rather
                    than a full week, so it is labelled for what it is.
                    Grey means still to come, so the shape of the month is
                    visible from day one rather than filling in from nothing.
                    Sized down on narrow screens: "70.3%" does not fit five
                    across on a phone at the desktop size. */}
                <div className="grid grid-cols-5 gap-1 sm:gap-2 mt-4">
                    {weeks.map(w => (
                        <div key={w.week}
                             className={`rounded-lg border px-0 sm:px-1 py-2 text-center ${
                                 w.current ? 'border-amber-300 bg-amber-50'
                                     : w.score != null ? 'border-stone-200 bg-white'
                                         : 'border-stone-100 bg-stone-50'}`}>
                            <p className="text-[9px] sm:text-[10px] uppercase tracking-wide text-stone-500 leading-none">
                                {w.week === weeks.length ? 'Final' : `Week ${w.week}`}
                            </p>
                            <p className={`text-sm sm:text-lg font-bold leading-tight mt-1 tabular-nums tracking-tight ${
                                w.score != null ? getScoreColor(w.score) : 'text-stone-300'}`}>
                                {w.score != null ? (
                                    <>
                                        {w.score.toFixed(1)}
                                        <span className="text-[9px] sm:text-xs font-semibold align-top">%</span>
                                    </>
                                ) : w.current ? (
                                    <button type="button" onClick={onPractise}
                                            aria-label="Run this week's challenge phrases"
                                            className="inline-flex items-center justify-center text-amber-500 hover:text-amber-600 p-1 -m-1">
                                        <Play className="h-4 w-4 sm:h-5 sm:w-5 fill-current" />
                                    </button>
                                ) : '—'}
                            </p>
                        </div>
                    ))}
                </div>

                <ChallengeCalendar days={challenge.days} target={challenge.daily_minutes}
                                   onPractise={onPractise} />

                <Button onClick={onPractise}
                        className="w-full mt-3 bg-amber-500 hover:bg-amber-600 text-white">
                    <Play className="h-4 w-4 mr-2 fill-current" />
                    {baseline == null ? 'Set my starting score' : 'Practise challenge phrases'}
                </Button>
            </CardContent>
        </Card>
    );
};

const CalendarHeatmap = ({ data }) => {
    const cells = useMemo(() => {
        if (!data?.length) return [];
        const firstDay = new Date(data[0].date + 'T00:00:00');
        const startPad = (firstDay.getDay() + 6) % 7; // Mon=0
        return [...Array(startPad).fill(null), ...data];
    }, [data]);

    const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

    // Capped at its half-card width: widened to a full row on a desktop, the
    // squares would otherwise grow with it.
    return (
        <div className="max-w-[420px] mx-auto">
            <div className="grid grid-cols-7 gap-1 mb-1">
                {DAY_LABELS.map((l, i) => (
                    <div key={i} className="text-[9px] text-stone-400 text-center">{l}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {cells.map((day, i) =>
                    day ? (
                        <div
                            key={i}
                            className={`aspect-square rounded-sm ${heatColor(day.count)}`}
                        />
                    ) : (
                        <div key={i} className="aspect-square" />
                    )
                )}
            </div>
        </div>
    );
};

// Starter checklist shown after a new user completes onboarding. Dismissable —
// once cleared it stays cleared (localStorage keyed by user id).
const StarterChecklist = ({ userId, items, onDismiss }) => {
    const handleDismiss = () => {
        // Simple browser confirm — the checklist is easy to lose accidentally
        // and there's no way to bring it back once dismissed, so a quick
        // "are you sure" saves the "oh no I closed it" moment.
        if (window.confirm("Hide these setup tips? You won't be able to bring them back.")) {
            onDismiss();
        }
    };
    return (
        <Card className="bg-white border-primary/20 shadow-sm">
            <CardContent className="p-4 md:p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                        <p className="text-sm font-semibold text-foreground">Get comfortable using Mimique</p>
                        <p className="text-xs text-stone-500 mt-0.5">A few quick things to get you going.</p>
                    </div>
                    <button
                        type="button"
                        aria-label="Dismiss checklist"
                        onClick={handleDismiss}
                        className="text-stone-400 hover:text-stone-600 flex-shrink-0 p-1 -m-1"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <ul className="space-y-1">
                    {items.map(item => (
                        <li key={item.id}>
                            <button
                                type="button"
                                onClick={item.onClick}
                                className={`w-full flex items-center gap-3 text-left rounded-lg px-2 py-2 transition-colors hover:bg-stone-50 ${
                                    item.done ? 'text-stone-400' : 'text-stone-700'
                                } ${item.pulsate && !item.done ? 'animate-onboarding-glow' : ''}`}
                            >
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    item.done ? 'bg-emerald-100 text-emerald-600' : 'bg-stone-100 text-stone-400'
                                }`}>
                                    {item.done
                                        ? <CheckCircle2 className="h-4 w-4" />
                                        : <item.icon className="h-3.5 w-3.5" />}
                                </span>
                                <span className="flex-1 min-w-0">
                                    <span className={`block text-sm ${item.done ? 'line-through' : ''}`}>{item.label}</span>
                                    {item.sublabel && <span className="block text-xs text-stone-400">{item.sublabel}</span>}
                                </span>
                                {!item.done && <ArrowRight className="h-3.5 w-3.5 text-stone-400 flex-shrink-0" />}
                            </button>
                        </li>
                    ))}
                </ul>
            </CardContent>
        </Card>
    );
};

// Launch banners: shown above the checklist. PhraseCapBanner appears when a
// Free or Pro user has more phrases in the DB than their plan's cap allows.
// FoundingWelcomeBanner shows once per account for anyone flagged
// founding_member, then hides on dismiss.
// The buttons link straight to Stripe checkout with the account email
// prefilled. The pricing table is a secondary "compare the plans" link, not the
// main CTA, and needs the "/#pricing" exception in App.js to open at all: the
// landing route otherwise redirects logged-in users back here.
//
// Free over cap means genuinely hidden phrases (existing beta accounts from
// before the cap existed) — they come back on upgrade. Pro over cap (e.g.
// downgraded from Unlimited after building up a bigger library) never hides
// anything, it just blocks adding more, so the copy and CTA differ: no
// "come back" framing, and no pointless "Upgrade to Pro" when they're on it.
// Small print under the founding welcome banner's buttons. The phrase-cap
// banner used to share it and now goes through the shared upgrade dialog
// instead, which carries the same promise and the same link.
// New tab on purpose: comparing plans shouldn't cost them the dashboard.
const PlanCompareNote = ({ accent = 'text-amber-700 hover:text-amber-800' }) => (
    <p className="mt-2 text-xs text-stone-500">
        Cancel any time.{' '}
        <a
            href={PRICING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`font-semibold underline underline-offset-2 ${accent}`}
        >
            Compare the plans
        </a>
    </p>
);

const PhraseCapBanner = ({ totalInDb, cap, plan }) => {
    const isPro = plan === 'pro';
    const [capUpgradeOpen, setCapUpgradeOpen] = useState(false);
    return (
        <Card className="bg-amber-50 border-amber-200 shadow-sm">
            <CardContent className="p-4 flex items-start gap-3">
                <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                    <Lock className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0">
                    <p className="text-sm text-stone-800">
                        {isPro ? (
                            <>You're over the Pro plan's {cap}-phrase limit. All your existing phrases still work, but you'll need Unlimited to add more.</>
                        ) : (
                            <>The Free plan has a limit of {cap} phrases, your other {Math.max(0, totalInDb - cap)} phrases are still stored in the database and come straight back the moment you upgrade.</>
                        )}
                    </p>
                    {canCheckout() && (
                        <>
                            {/* One button into the shared dialog, like every
                                other lock in the app. The banner used to carry
                                its own two Stripe links, its own prices and its
                                own small print, which is three places for the
                                same promise to drift. */}
                            {/* The app's green, like every other upgrade button.
                                Amber-600 on an amber card read as orange and as
                                a warning, when it is the way out of one. */}
                            <Button size="sm" onClick={() => setCapUpgradeOpen(true)}
                                    className="rounded-full text-xs h-7 px-3 mt-2.5">
                                {isPro ? 'Go Unlimited' : 'Unlock more phrases'}
                            </Button>
                            <UpgradeDialog
                                open={capUpgradeOpen}
                                onOpenChange={setCapUpgradeOpen}
                                title={isPro ? 'Room for more phrases' : 'Get more phrases'}
                                message={isPro
                                    ? `You are over the Pro plan's ${cap}-phrase limit. Everything you have still works, but Unlimited is what lets you add more.`
                                    : `The Free plan holds ${cap} phrases. Your other ${Math.max(0, totalInDb - cap)} are still stored and come straight back the moment you upgrade.`}
                            />
                        </>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

// Solid background on purpose: the dashboard sits on a dark gradient, so a
// translucent tint (bg-primary/5) left dark text unreadable.
const FoundingWelcomeBanner = ({ email, userId, onDismiss }) => (
    <Card className="bg-emerald-50 border-emerald-200 shadow-sm">
        <CardContent className="p-4 flex items-start gap-3">
            <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                <Gift className="h-4 w-4" />
            </span>
            <div className="flex-1 min-w-0">
                <p className="text-sm text-stone-800 leading-relaxed">
                    <strong>Welcome back, founding member 💚</strong><br />
                    Thank you for testing Mimique, for your honest feedback, and for helping shape it
                    into what it is today. It genuinely would not be the same without you. I really
                    hope you'll keep going on this journey with me.
                </p>
                {canCheckout() && (
                <>
                <p className="text-sm text-stone-800 mt-2">
                    Use code <span className="font-mono font-semibold">FOUNDERS50</span> at checkout for 50% off for 12 months.
                </p>
                {/* The same two buttons as every other upgrade prompt, so an
                    upgrade looks like an upgrade wherever it is offered. */}
                <div className="mt-2.5">
                    <UpgradeButtons />
                </div>
                <PlanCompareNote accent="text-emerald-700 hover:text-emerald-800" />
                </>
                )}
            </div>
            <button
                onClick={onDismiss}
                className="text-stone-400 hover:text-stone-600 flex-shrink-0"
                aria-label="Dismiss"
            >
                <X className="h-4 w-4" />
            </button>
        </CardContent>
    </Card>
);

// Offers the setup wizard to accounts that never had it (marked onboarded by
// the August backfill). Optional and dismissable. The numbers come from
// /onboarding/redo, so the space check lives server side.
const RedoOnboardingBanner = ({ info, onStart, onDismiss }) => {
    const noRoom = info.max_topics < 1;
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    return (
        <Card className="bg-emerald-50 border-emerald-200 shadow-sm">
            <CardContent className="p-4 flex items-start gap-3">
                <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0 text-sm text-stone-800 leading-relaxed">
                    <p>
                        <strong>Mimique setup wizard</strong><br />
                        You joined before the onboarding questions existed. This is a quick, one-off series of
                        questions to customise Mimique for you and add some ready-made collections of phrases
                        based on your level and interests.
                    </p>
                    <p className="mt-2">
                        Note: running this will replace your current settings.
                    </p>
                    {noRoom ? (
                        <p className="mt-2 font-semibold text-amber-800">
                            You don't have enough space to add more phrases via this wizard. A starter collection
                            needs {info.seed_per_topic} free phrases and you have {info.slots} free spaces. Delete
                            some to make space or{' '}
                            <button type="button" onClick={() => setUpgradeOpen(true)} className="underline hover:no-underline">
                                upgrade here
                            </button>.
                            <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} title="Get more phrases" />
                        </p>
                    ) : (
                        <Button size="sm" onClick={onStart} className="rounded-full text-xs h-7 px-3 mt-2.5">
                            Start setup
                        </Button>
                    )}
                </div>
                <button
                    onClick={onDismiss}
                    className="text-stone-400 hover:text-stone-600 flex-shrink-0"
                    aria-label="Dismiss"
                >
                    <X className="h-4 w-4" />
                </button>
            </CardContent>
        </Card>
    );
};

// ── Main component ───────────────────────────────────────────────────────────

const Dashboard = () => {
    const { user } = useAuth();
    const [wordsUpgradeOpen, setWordsUpgradeOpen] = useState(false);
    const [soundsUpgradeOpen, setSoundsUpgradeOpen] = useState(false);
    const navigate = useNavigate();
    const { canInstall, installed: appInstalled, promptInstall } = useInstallPrompt();
    const [dash, setDash] = useState(null);
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isNewUser] = useState(() => { const v = sessionStorage.getItem('newUser') === '1'; if (v) sessionStorage.removeItem('newUser'); return v; });
    const checklistKey = user?.id ? `onboarding_checklist_dismissed_${user.id}` : null;
    const [checklistDismissed, setChecklistDismissed] = useState(
        () => !!(checklistKey && localStorage.getItem(checklistKey) === '1')
    );
    const [reminderTimeSet, setReminderTimeSet] = useState(null); // null = unknown yet
    // Founding-welcome banner is dismissable and stays dismissed per user.
    const foundingBannerKey = user?.id ? `founding_welcome_dismissed_${user.id}` : null;
    const [foundingBannerDismissed, setFoundingBannerDismissed] = useState(
        () => !!(foundingBannerKey && localStorage.getItem(foundingBannerKey) === '1')
    );
    const dismissFoundingBanner = () => {
        if (foundingBannerKey) localStorage.setItem(foundingBannerKey, '1');
        setFoundingBannerDismissed(true);
    };
    // Setup wizard offer for accounts that never had it. Dismissable per user.
    const redoKey = user?.id ? `redo_onboarding_dismissed_${user.id}` : null;
    const [redoInfo, setRedoInfo] = useState(null);
    useEffect(() => {
        let dismissed = false;
        try { dismissed = !!(redoKey && localStorage.getItem(redoKey) === '1'); } catch { /* storage off */ }
        if (!user?.can_redo_onboarding || dismissed) return;
        onboardingApi.getRedo()
            .then(res => { if (res.data.eligible) setRedoInfo(res.data); })
            .catch(() => { /* the offer is optional, nothing to show */ });
    }, [user?.can_redo_onboarding, redoKey]);
    const dismissRedo = () => {
        try { if (redoKey) localStorage.setItem(redoKey, '1'); } catch { /* storage off */ }
        setRedoInfo(null);
    };
    // Holds a November place: shows the countdown until the challenge card
    // takes over on day 1.
    const [challengeJoined, setChallengeJoined] = useState(false);
    useEffect(() => {
        if (!user?.id) return;
        challengeApi.me()
            .then(res => setChallengeJoined(!!res.data.joined))
            .catch(() => { /* optional, nothing to show */ });
    }, [user?.id]);
    // Per-user set of checklist item ids the user has tapped. Tapped items
    // stay crossed out on return, on top of any auto-detected done state (like
    // "practice done" from lib.total_practices).
    const clickedKey = user?.id ? `onboarding_checklist_clicked_${user.id}` : null;
    const [clickedItems, setClickedItems] = useState(() => {
        if (!clickedKey) return new Set();
        try {
            const raw = localStorage.getItem(clickedKey);
            return new Set(raw ? JSON.parse(raw) : []);
        } catch { return new Set(); }
    });
    const markClicked = (id) => {
        if (!clickedKey) return;
        setClickedItems(prev => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            try { localStorage.setItem(clickedKey, JSON.stringify([...next])); } catch {}
            // Notify same-tab listeners (e.g. FeedbackButton's help-icon pulse
            // wants to stop the moment the "help" item is marked clicked).
            try { window.dispatchEvent(new CustomEvent('onboarding-checklist-updated')); } catch {}
            return next;
        });
    };

    // Real-completion state ("done" per checklist item). Kept separate from
    // clickedItems so we can distinguish "user tapped a checklist row"
    // (drives things like the help-icon pulse) from "user actually completed
    // the corresponding action" (drives the crossed-out tick). Other pages
    // mark items done via the shared `onboarding-checklist-updated` event.
    const doneKey = user?.id ? `onboarding_checklist_done_${user.id}` : null;
    const [doneItems, setDoneItems] = useState(() => {
        if (!doneKey) return new Set();
        try {
            const raw = localStorage.getItem(doneKey);
            return new Set(raw ? JSON.parse(raw) : []);
        } catch { return new Set(); }
    });
    useEffect(() => {
        if (!doneKey) return;
        const refresh = () => {
            try {
                const raw = localStorage.getItem(doneKey);
                setDoneItems(new Set(raw ? JSON.parse(raw) : []));
            } catch {}
        };
        window.addEventListener('onboarding-checklist-updated', refresh);
        window.addEventListener('storage', refresh);
        return () => {
            window.removeEventListener('onboarding-checklist-updated', refresh);
            window.removeEventListener('storage', refresh);
        };
    }, [doneKey]);

    // ?highlight=practice pulses the Start Practice Session button — same
    // pattern as the /collections page uses for Add Phrase / AI Generate.
    const [dashParams, setDashParams] = useSearchParams();
    const dashHighlight = dashParams.get('highlight');
    const clearDashHighlight = () => {
        if (!dashParams.get('highlight')) return;
        const next = new URLSearchParams(dashParams);
        next.delete('highlight');
        setDashParams(next, { replace: true });
    };
    const [practiceDialogOpen, setPracticeDialogOpen] = useState(false);
    const [practiceFlow, setPracticeFlow] = useState(getSavedFlow);
    const [practiceMode, setPracticeMode] = useState('listen-record');
    const [practiceOrder] = useState(getSavedOrder);
    const [selectedPracticeCollections, setSelectedPracticeCollections] = useState([]);

    useEffect(() => {
        let cancelled = false;
        Promise.all([dashboardApi.get(), collectionsApi.getAll()])
            .then(([dashRes, collRes]) => {
                if (cancelled) return;
                setDash(dashRes.data);
                setCollections(collRes.data);
            })
            .catch(() => { if (!cancelled) toast.error('Failed to load dashboard'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    // Only fetched to know whether "Set a reminder" is already done. Cheap
    // one-shot call, only fired when the checklist would actually be shown.
    useEffect(() => {
        if (checklistDismissed) return;
        settingsApi.get()
            .then(res => setReminderTimeSet(!!res.data.reminder_time))
            .catch(() => setReminderTimeSet(false));
    }, [checklistDismissed]);

    // ── Customisable layout ────────────────────────────────────────────────
    // Which sections show, and in what order. Kept in the learner's settings so
    // it follows them across devices; a copy in localStorage stops the default
    // layout flashing before the settings arrive.
    const LAYOUT_CACHE = 'dashboard_layout';
    const [layout, setLayout] = useState(() => {
        try {
            const c = JSON.parse(localStorage.getItem(LAYOUT_CACHE) || 'null');
            if (c && Array.isArray(c.order) && Array.isArray(c.hidden)) return c;
        } catch { /* storage unavailable: fall through to the default */ }
        return { order: [], hidden: [] };
    });
    const [customiseOpen, setCustomiseOpen] = useState(false);

    useEffect(() => {
        settingsApi.get()
            .then(res => {
                const next = { order: res.data.dashboard_order || [], hidden: res.data.dashboard_hidden || [] };
                setLayout(next);
                try { localStorage.setItem(LAYOUT_CACHE, JSON.stringify(next)); } catch { /* ignore */ }
            })
            .catch(() => {})
    }, []);

    const changeLayout = (next) => {
        setLayout(next);
        try { localStorage.setItem(LAYOUT_CACHE, JSON.stringify(next)); } catch { /* ignore */ }
        settingsApi.update({ dashboard_order: next.order, dashboard_hidden: next.hidden })
            .catch(() => toast.error('Could not save your dashboard layout'));
    };

    // One section for the grid. `full` spans both columns on a wide screen. A
    // hidden section, or one with no data to show, has no node and takes no
    // cell. SectionGrid lays them out.
    const sectionOrder = orderSections(layout.order);
    const slot = (id, full, node) => ({ id, full, node: layout.hidden.includes(id) ? null : node });

    const dismissChecklist = () => {
        if (checklistKey) localStorage.setItem(checklistKey, '1');
        setChecklistDismissed(true);
    };

    const handleStartPractice = () => {
        if (!dash?.library?.total_phrases) {
            navigate('/collections');
            return;
        }
        if (collections.length === 0) {
            navigate(`${flowPath(getSavedFlow())}?order=${getSavedOrder()}&mode=listen-record`);
        } else {
            setPracticeDialogOpen(true);
        }
    };

    // Quick-launch a session (weakest phrases, a collection, …) using the saved
    // flow, always returning here afterwards.
    const startSession = (params) => {
        const qs = new URLSearchParams({ ...params, returnTo: 'dashboard' });
        navigate(`${flowPath(getSavedFlow())}?${qs.toString()}`);
    };

    const handlePracticeWithSelection = () => {
        saveFlow(practiceFlow);
        const params = new URLSearchParams();
        params.set('order', practiceOrder);
        // Mode only applies to the automatic hands-free loop.
        if (practiceFlow === 'automatic') params.set('mode', practiceMode);
        if (selectedPracticeCollections.length > 0) {
            params.set('collections', selectedPracticeCollections.join(','));
        }
        navigate(`${flowPath(practiceFlow)}?${params.toString()}`);
    };

    // Derived values
    const streak = dash?.streak ?? {};
    const weekly = dash?.weekly ?? {};
    const lib = dash?.library ?? {};
    // The badge shows a locked teaser to Free, since only Speechace scores a
    // band and Free is scored by Gemini.
    const isFreePlan = (dash?.plan?.name || user?.plan || 'free') === 'free';
    const sessionGoalMinutes = getSavedSessionLength();
    const todayMinutes = Math.round(streak.today_minutes ?? 0);
    const todayComplete = todayMinutes >= sessionGoalMinutes;

    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const today = new Date();
    const todayWeekday = (today.getDay() + 6) % 7; // Mon=0
    const weekDaysPracticed = useMemo(() => {
        if (!dash?.heatmap) return new Set();
        const today = new Date();
        const monday = new Date(today);
        monday.setDate(today.getDate() - todayWeekday);
        const dayStrs = new Set();
        for (let i = 0; i < 7; i++) {
            const d = new Date(monday);
            d.setDate(monday.getDate() + i);
            dayStrs.add(d.toISOString().slice(0, 10));
        }
        return new Set(dash.heatmap.filter(h => h.count > 0 && dayStrs.has(h.date)).map(h => {
            const d = new Date(h.date + 'T00:00:00');
            return (d.getDay() + 6) % 7; // Mon=0
        }));
    }, [dash, todayWeekday]);

    // Short, contextual encouragement per card — warm nudges to keep going.
    const todayEncourage = todayComplete
        ? "Come back tomorrow to keep it up."
        : todayMinutes > 0
            ? "Nearly there, just one more short session."
            : "Just a few minutes gets you going today.";

    const streakEncourage = "Consistency is key.";

    const weekTarget = weekly.target ?? 5;
    const weekLeft = Math.max(0, weekTarget - weekDaysPracticed.size);
    const weekEncourage = weekLeft === 0
        ? "Weekly goal done. Brilliant!"
        : `${weekLeft} more day${weekLeft === 1 ? '' : 's'} to hit your weekly goal.`;

    const activeDays30 = (dash?.heatmap ?? []).slice(-30).filter(d => d.count > 0).length;
    const heatEncourage = activeDays30 >= 20
        ? "Fantastic consistency this month."
        : activeDays30 > 0
            ? `${activeDays30} active day${activeDays30 === 1 ? '' : 's'} in the last 30.`
            : "Fill in your first square today.";

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-foreground">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-primary-foreground/70">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-fixed bg-gradient-to-b from-foreground to-primary text-primary-foreground" data-testid="dashboard">
            <header className="sticky top-0 z-40 bg-foreground/90 backdrop-blur-md border-b border-primary-foreground/10">
                <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-start gap-3">
                    {/* Mobile only — on desktop the sidebar already shows the icon,
                        and both at once was a duplicate. */}
                    <MimiqueIcon size={48} variant="square" className="md:hidden" style={{ flexShrink: 0 }} />
                    <div className="min-w-0">
                        <h1 className="text-sm md:text-base font-bold text-primary-foreground">{isNewUser ? 'Welcome' : 'Welcome back'}, {user?.name}</h1>
                        {/* Mobile only — on desktop the sidebar shows these,
                            and both at once was a duplicate. Always its own row
                            under the greeting, not wrapped in beside it. */}
                        <div className="flex flex-wrap items-center gap-1.5 mt-1 md:hidden">
                            <PlanBadge plan={dash?.plan?.name} onDark />
                            {dash?.plan?.founding_member && <FoundingBadge onDark />}
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-5xl mx-auto px-4 md:px-6 pt-4 animate-fade-in flex md:justify-center">
                <Button
                    data-testid="start-practice-btn"
                    className={`w-full md:w-auto rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold text-base shadow-md px-10 py-3 h-auto ${
                        dashHighlight === 'practice' ? 'animate-onboarding-glow' : ''
                    }`}
                    onClick={() => { clearDashHighlight(); handleStartPractice(); }}
                >
                    <Play className="h-5 w-5 mr-1" />
                    Start Practice Session
                </Button>
            </div>

            <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-5 md:space-y-6 stagger-children">

                {/* Launch banners — phrase cap for over-cap Free users, and a
                    founding-member welcome (dismissable). Both driven by
                    dash.plan from the backend. */}
                {dash?.plan?.over_cap && (
                    <PhraseCapBanner
                        totalInDb={dash.library?.total_phrases_in_db ?? 0}
                        cap={dash.plan?.phrase_cap ?? 50}
                        plan={dash.plan?.name}
                    />
                )}
                {/* Free plan only: the banner exists to sell the founding
                    discount, so it goes away once they've taken a paid plan
                    rather than asking them to upgrade again. */}
                {dash?.plan?.founding_member && dash?.plan?.name === 'free' && !foundingBannerDismissed && (
                    <FoundingWelcomeBanner
                        email={user?.email}
                        userId={user?.id}
                        onDismiss={dismissFoundingBanner}
                    />
                )}
                {redoInfo && (
                    <RedoOnboardingBanner
                        info={redoInfo}
                        onStart={() => navigate('/onboarding?redo=1')}
                        onDismiss={dismissRedo}
                    />
                )}

                {/* Starter checklist — visible until dismissed, guides new users to the
                    four things worth trying on day 1. Auto-completes items where we
                    have signal (practice count, reminder set). */}
                {!checklistDismissed && (
                    <StarterChecklist
                        userId={user?.id}
                        onDismiss={dismissChecklist}
                        items={[
                            {
                                // Rule: an item only ticks off when the ACTUAL action is
                                // completed, never on the row tap itself. Row tap only
                                // navigates + pulses the correct target button/icon.
                                id: 'practice',
                                label: 'Do your first practice session',
                                icon: Play,
                                done: (lib.total_practices ?? 0) > 0 || doneItems.has('practice'),
                                // Already on /dashboard — flip the URL flag so the Start
                                // Practice Session button pulses; don't tick until they
                                // actually complete a session.
                                onClick: () => setDashParams({ highlight: 'practice' }, { replace: true }),
                            },
                            {
                                id: 'add_phrase',
                                label: 'Add a phrase of your own',
                                icon: Plus,
                                // Ticked only after they successfully submit Add Phrase on /collections.
                                done: doneItems.has('add_phrase'),
                                onClick: () => navigate('/collections?highlight=add-phrase'),
                            },
                            {
                                id: 'ai_generate',
                                label: 'Generate a collection of phrases using AI for any subject you want',
                                icon: Sparkles,
                                // Ticked only after they successfully generate on /collections.
                                done: doneItems.has('ai_generate'),
                                onClick: () => navigate('/collections?highlight=ai-generate'),
                            },
                            {
                                id: 'settings_visited',
                                label: 'Familiarise yourself with the different options in Settings',
                                icon: Bell,
                                // Ticked when they actually land on /settings (page marks itself done).
                                done: doneItems.has('settings_visited'),
                                onClick: () => navigate('/settings'),
                            },
                            {
                                id: 'help_visited',
                                label: 'Find where help videos and support live',
                                icon: HelpCircle,
                                // Ticked only after they click the pulsing help icon (top right),
                                // which marks 'help_visited' done and opens the modal.
                                done: doneItems.has('help_visited'),
                                // Do NOT navigate. Row tap triggers the help icon's pulse
                                // via the 'help_wanted' clicked flag.
                                onClick: () => markClicked('help_wanted'),
                            },
                            {
                                id: 'install_home_screen',
                                label: 'Add Mimique to home screen',
                                sublabel: 'The full app on your home screen, straight to your practice',
                                icon: Download,
                                done: appInstalled,
                                // Chrome/Edge: trigger the real install dialog directly.
                                // iOS/desktop-without-prompt: send them to Settings, which
                                // has the Share-sheet instructions for those cases.
                                onClick: canInstall ? promptInstall : () => navigate('/settings'),
                            },
                        ]}
                    />
                )}

                {challengeJoined && dash && !dash.challenge && Date.now() < new Date(CHALLENGE_STARTS).getTime() && (
                    <ChallengeCountdownCard />
                )}

                {dash && !dash.challenge && (dash.plan?.challenge_day || dash.plan?.challenge_waiting) && (
                    <ChallengeWaitingCard day={dash.plan.challenge_day}
                                          length={dash.plan.challenge_length || 30}
                                          onPractise={handleStartPractice} />
                )}

                {dash?.challenge && (
                    <ChallengeCard
                        challenge={dash.challenge}
                        onPractise={() => {
                            // Fixed order, not smart order, so the same phrases
                            // come round in the same sequence every week and the
                            // weekly scores are actually comparable.
                            const params = new URLSearchParams({
                                collections: dash.challenge.collection_id,
                                order: 'added',
                                mode: 'listen-record',
                                returnTo: 'dashboard',
                            });
                            navigate(`${flowPath(getSavedFlow())}?${params.toString()}`);
                        }}
                    />
                )}

                {/* ── Row 1: Library stats ───────────────────────────────── */}
                <div className="flex justify-end -mb-1">
                    <button
                        type="button"
                        data-testid="customise-dashboard-btn"
                        onClick={() => setCustomiseOpen(true)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-foreground/70 hover:text-primary-foreground"
                    >
                        <SlidersHorizontal className="h-3.5 w-3.5" />Customise
                    </button>
                </div>

                {/* The customisable sections. Order and visibility come from the learner's
                    settings; each section is wrapped by slot(), which sets its place in
                    the grid and leaves an empty cell when it is hidden or has no data. */}
                <SectionGrid order={sectionOrder}>
                {slot('stats', true,
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {/* The collection count rides in the same box: it answers
                        "how is my library organised", which is the same question
                        as "how big is it", and it does not earn a tile of its own. */}
                    <StatCard icon={BookOpen} iconBg="bg-primary/10" iconColor="text-primary"
                        value={lib.total_phrases ?? 0} label="Total Phrases"
                        action={lib.total_collections ? (
                            <p className="text-xs text-stone-500 leading-tight">
                                in <span className="font-semibold text-stone-700">{lib.total_collections}</span>
                                {' '}{lib.total_collections === 1 ? 'collection' : 'collections'}
                            </p>
                        ) : null}
                        onClick={lib.total_phrases ? () => navigate('/collections') : undefined} />
                    <StatCard icon={TrendingUp} iconBg="bg-emerald-100" iconColor="text-emerald-600"
                        value={lib.total_practices ? `${lib.average_score ?? 0}%` : '—'}
                        valueClass={lib.total_practices ? getScoreColor(lib.average_score ?? 0) : 'text-stone-400'}
                        label="Avg Score" />
                    <StatCard icon={Star} iconBg="bg-amber-100" iconColor="text-amber-500 fill-amber-400"
                        value={lib.mastered ?? 0} label="Mastered"
                        onClick={lib.mastered ? () => navigate('/collections?filter=mastered') : undefined} />
                    <StatCard icon={Mic} iconBg="bg-accent/10" iconColor="text-accent"
                        value={lib.total_practices ?? 0} label="Sessions" />
                </div>
                )}

                {/* ── Row 2: Today + Streak ──────────────────────────────── */}

                    {/* Today's status — tap to start a session */}
                    {slot('today', false,
                    <Card
                        className="bg-white border-stone-100 shadow-sm cursor-pointer hover:shadow-md hover:border-primary/30 active:scale-[0.99] transition-all"
                        onClick={handleStartPractice}
                        data-testid="today-card"
                    >
                        <CardContent className="p-5">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
                                    <Target className="h-4 w-4 text-accent" />
                                </div>
                                <span className="text-sm font-semibold text-stone-700">Today</span>
                                {!todayComplete && (
                                    <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold px-3 py-1">
                                        <Play className="h-3 w-3" /> Practise
                                    </span>
                                )}
                            </div>
                            {todayComplete ? (
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                                    <div>
                                        <p className="text-base font-bold text-emerald-600">Goal complete!</p>
                                        <p className="text-xs text-stone-400">{todayMinutes} min practiced today</p>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <p className="text-2xl font-bold text-foreground">
                                        {todayMinutes} <span className="text-sm font-normal text-stone-500">of {sessionGoalMinutes} min</span>
                                    </p>
                                    <p className="text-xs text-stone-400 mt-0.5">Daily goal: {sessionGoalMinutes} minutes</p>
                                    <div className="mt-2 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-accent rounded-full transition-all"
                                            style={{ width: `${Math.min(100, (todayMinutes / sessionGoalMinutes) * 100)}%` }}
                                        />
                                    </div>
                                </div>
                            )}
                            <p className="text-xs text-accent/80 mt-3 font-medium">{todayEncourage}</p>
                        </CardContent>
                    </Card>
                    )}

                    {/* Streak — tap to start a session */}
                    {slot('streak', false,
                    <Card
                        className="bg-white border-stone-100 shadow-sm cursor-pointer hover:shadow-md hover:border-primary/30 active:scale-[0.99] transition-all"
                        onClick={handleStartPractice}
                        data-testid="streak-card"
                    >
                        <CardContent className="p-5">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                                    <Flame className="h-4 w-4 text-amber-500" />
                                </div>
                                <span className="text-sm font-semibold text-stone-700">Streak</span>
                                {!todayComplete && (
                                    <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold px-3 py-1">
                                        <Play className="h-3 w-3" /> Practise
                                    </span>
                                )}
                            </div>
                            <div className="flex items-end gap-6">
                                <div>
                                    <p className="text-3xl font-bold text-foreground">{streak.current ?? 0}</p>
                                    <p className="text-xs text-stone-400">current days</p>
                                </div>
                                <div className="pb-0.5">
                                    <div className="flex items-center gap-1">
                                        <Trophy className="h-3.5 w-3.5 text-amber-400" />
                                        <p className="text-lg font-semibold text-stone-600">{streak.longest ?? 0}</p>
                                    </div>
                                    <p className="text-xs text-stone-400">longest</p>
                                </div>
                            </div>
                            <p className="text-xs text-amber-600/90 mt-3 font-medium">{streakEncourage}</p>
                        </CardContent>
                    </Card>
                    )}

                {/* ── Row 3: Weekly progress + Heatmap ──────────────────── */}
                {slot('week', false,
                <Card
                    className="bg-white border-stone-100 shadow-sm cursor-pointer hover:shadow-md hover:border-primary/30 active:scale-[0.99] transition-all"
                    onClick={handleStartPractice}
                    data-testid="week-card"
                >
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center">
                                <TrendingUp className="h-3.5 w-3.5 text-primary" />
                            </div>
                            <span className="text-sm font-semibold text-stone-700">This Week</span>
                            {weekDaysPracticed.size < (weekly.target ?? 5) && (
                                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold px-3 py-1">
                                    <Play className="h-3 w-3" /> Practise
                                </span>
                            )}
                        </div>
                        <div className="flex gap-1.5">
                            {weekDays.map((day, idx) => {
                                const isPracticed = weekDaysPracticed.has(idx);
                                const isPast = idx <= todayWeekday;
                                return (
                                    <div key={day} className="flex-1 flex flex-col items-center gap-1">
                                        <div className={`w-full h-8 rounded-lg flex items-center justify-center text-xs font-medium transition-all ${
                                            isPracticed ? 'bg-primary text-white' :
                                            isPast ? 'bg-stone-100 text-stone-400' :
                                            'bg-stone-50 text-stone-300'
                                        }`}>
                                            {isPracticed ? '✓' : ''}
                                        </div>
                                        <span className="text-[10px] text-stone-400">{day}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="text-xs text-stone-500 mt-3 text-center">
                            {weekDaysPracticed.size} of {weekly.target ?? 5} day{(weekly.target ?? 5) === 1 ? '' : 's'} this week
                        </p>
                        <p className="text-xs text-primary/90 mt-1 text-center font-medium">{weekEncourage}</p>
                    </CardContent>
                </Card>
                )}

                {/* Heatmap */}
                {slot('heatmap', false,
                <Card className="bg-white border-stone-100 shadow-sm">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center">
                                <Flame className="h-3.5 w-3.5 text-primary" />
                            </div>
                            <span className="text-sm font-semibold text-stone-700">Last 30 Days</span>
                        </div>
                        <CalendarHeatmap data={(dash?.heatmap ?? []).slice(-30)} />
                        <p className="text-xs text-primary/90 mt-3 text-center font-medium">{heatEncourage}</p>
                    </CardContent>
                </Card>
                )}

                {/* ── Phrases with lowest scores (weakest) ───────────────── */}
                {slot('weak_phrases', true, dash?.weakest_phrases?.length > 0 && (
                    <Card className="bg-white border-stone-100 shadow-sm">
                        <CardContent className="p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-7 h-7 bg-amber-100 rounded-lg flex items-center justify-center">
                                    <Target className="h-3.5 w-3.5 text-amber-600" />
                                </div>
                                <span className="text-sm font-semibold text-stone-700">Phrases with lowest scores</span>
                            </div>
                            {/* Scrollable list — cap the visible height at roughly five
                                rows so the card doesn't grow off-screen when we return
                                up to 10 weakest phrases. Users scroll inside for the rest. */}
                            <div className="space-y-2.5 max-h-56 overflow-y-auto pr-1">
                                {dash.weakest_phrases.map(p => (
                                    <div key={p.id} className="flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="text-sm text-foreground font-medium truncate">{p.french}</p>
                                            {p.english && <p className="text-xs text-stone-400 truncate">{p.english}</p>}
                                        </div>
                                        <span className={`text-sm font-semibold flex-shrink-0 ${getScoreColor(p.average_score)}`}>
                                            {p.average_score}%
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <PractiseListButton
                                testId="practise-weakest-btn"
                                onClick={() => startSession({
                                    phrase_ids: dash.weakest_phrases.map(p => p.id).join(','),
                                    mode: 'listen-record',
                                })}>
                                Practise these {dash.weakest_phrases.length} phrases
                            </PractiseListButton>
                        </CardContent>
                    </Card>
                ))}

                {/* ── Words with lowest scores ───────────────────────────── */}
                {/* Sits under the phrases, because it is the finer grain of the
                    same question: which phrase, then which word inside it.

                    A phrase can sit at 80% with one word in it that never gets
                    above 30, and that word is what the next five minutes should
                    go on. Listen and record are right here so it can be worked
                    on without opening anything.

                    Free sees it locked rather than not at all: hiding it means
                    they never learn it exists. */}
                {slot('weak_words', true, (!isFreePlan ? dash?.weak_words?.length > 0 : true) && (
                    <LowestScoresCard
                        title="Words with lowest scores"
                        subtitle="How the words score inside your phrases."
                        isFreePlan={isFreePlan}
                        lockedRows={[['bonjour', 41], ['toujours', 52], ["aujourd'hui", 58], ['quelques', 63]]}
                        upgradeTitle="See the words letting you down"
                        upgradeMessage="Free scores the phrase as a whole, so there are no individual words to show here."
                        upgradeOpen={wordsUpgradeOpen}
                        setUpgradeOpen={setWordsUpgradeOpen}
                        // A session of the phrases these words were said in
                        // (Leanne, 4 Nov 2026). Same button as the phrases card.
                        footer={dash?.weak_words_phrase_ids?.length > 0 && (
                            <PractiseListButton
                                testId="practise-weak-words-btn"
                                onClick={() => startSession({
                                    phrase_ids: dash.weak_words_phrase_ids.join(','),
                                    mode: 'listen-record',
                                })}>
                                Practise phrases containing these words
                            </PractiseListButton>
                        )}>
                        {(dash?.weak_words || []).map(w => <WeakWordRow key={w.word} word={w} />)}
                    </LowestScoresCard>
                ))}


                {/* Sounds with lowest scores: the same idea as the words above,
                    one level down, averaged across every recording. Paid only,
                    locked for Free like the words card. Beta accounts only for
                    now, while it is tried out. */}
                {slot('weak_sounds', true, pronunciationBeta() && (isFreePlan || dash?.weak_sounds?.length > 0) && (
                    <LowestScoresCard
                        title="Sounds with lowest scores"
                        subtitle="How each sound scores across all your recordings."
                        isFreePlan={isFreePlan}
                        lockedRows={[['the U in tu', 44], ['the UN in brun', 51],
                                     ['the R in rue', 57], ['the EU in peu', 62]]}
                        upgradeTitle="See the sounds letting you down"
                        upgradeMessage="Pro and Unlimited track every sound across all your recordings, so you know exactly which ones to work on."
                        listHeight="max-h-72"
                        upgradeOpen={soundsUpgradeOpen}
                        setUpgradeOpen={setSoundsUpgradeOpen}>
                        {(dash?.weak_sounds || []).map(s => <WeakSoundRow key={s.sound} sound={s} />)}
                    </LowestScoresCard>
                ))}

                {/* ── Row 6: Top collections ─────────────────────────────── */}
                {slot('top_collections', true, dash?.top_collections?.length > 0 && (
                    <Card className="bg-white border-stone-100 shadow-sm">
                        <CardContent className="p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-7 h-7 bg-stone-100 rounded-lg flex items-center justify-center">
                                    <FolderOpen className="h-3.5 w-3.5 text-stone-500" />
                                </div>
                                <span className="text-sm font-semibold text-stone-700">Least Practiced Collections</span>
                            </div>
                            <div className="space-y-3">
                                {(() => {
                                    // Bar width is relative to the biggest count in the visible
                                    // list — was safe when sorted desc (max at index 0), needs
                                    // an explicit reduce now the list is sorted ascending.
                                    const maxPc = dash.top_collections.reduce((m, x) => (x.practice_count > m ? x.practice_count : m), 1);
                                    return dash.top_collections.map((c, i) => (
                                        <div
                                            key={c.id}
                                            role="button"
                                            onClick={() => startSession({ collections: c.id, mode: 'listen-record' })}
                                            className="group cursor-pointer -mx-2 px-2 py-1.5 rounded-lg hover:bg-stone-50 active:scale-[0.99] transition-all"
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm text-stone-700 font-medium truncate flex items-center gap-1.5">
                                                    <Play className="h-3 w-3 text-primary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                                    {c.name}
                                                </span>
                                                <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                                                    <span className="text-xs text-stone-400">{c.practice_count} plays</span>
                                                    {c.average_score != null && (
                                                        <span className={`text-xs font-medium ${getScoreColor(c.average_score)}`}>
                                                            {c.average_score}%
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-primary rounded-full"
                                                    style={{ width: `${Math.round((c.practice_count / maxPc) * 100)}%` }}
                                                />
                                            </div>
                                            {/* Mode breakdown — only render when there's any tracked mode data
                                                (older listens predate the per-mode counters, so the sum can be 0
                                                even when practice_count > 0; in that case we skip the line). */}
                                            {c.modes && (c.modes.listen_only + c.modes.listen_repeat + c.modes.listen_record) > 0 && (
                                                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-stone-500">
                                                    <span>Listen <span className="text-stone-700 font-medium">{c.modes.listen_only}</span></span>
                                                    <span>Repeat <span className="text-stone-700 font-medium">{c.modes.listen_repeat}</span></span>
                                                    <span>Record <span className="text-stone-700 font-medium">{c.modes.listen_record}</span></span>
                                                </div>
                                            )}
                                        </div>
                                    ));
                                })()}
                            </div>
                        </CardContent>
                    </Card>
                ))}
                </SectionGrid>


            </main>

            <DashboardCustomise
                open={customiseOpen}
                onOpenChange={setCustomiseOpen}
                layout={layout}
                onChange={changeLayout}
                showSounds={pronunciationBeta()}
            />

            {/* ── Practice dialog ─────────────────────────────────────────── */}
            <PracticeSessionDialog
                open={practiceDialogOpen}
                onOpenChange={setPracticeDialogOpen}
                collections={collections}
                totalPhrases={lib.total_phrases ?? 0}
                selectedPracticeCollections={selectedPracticeCollections}
                setSelectedPracticeCollections={setSelectedPracticeCollections}
                practiceFlow={practiceFlow}
                setPracticeFlow={setPracticeFlow}
                practiceMode={practiceMode}
                setPracticeMode={setPracticeMode}
                onStart={handlePracticeWithSelection}
            />
        </div>
    );
};

export default Dashboard;
